import React, { useEffect, useRef, useState } from "react";
import { useResearchTheme } from '../ui/useResearchTheme';
import { ResearchNav, StoryRow, StaleBadge } from '../ui/ResearchUI';
import { SpringRise } from '../../../lib/motion';
import "./researchDesk.css";
import "./researchFeed.css";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/research/editions/feed`;
const KEY = "aeon_research_feed_filters_v1";
const EMPTY = { asset: "", topic: "", publisher: "", importance: 0 };
const TOPICS = ["Macro", "ETF", "Regulation", "Security", "Network", "Derivatives", "Institutions", "Other"];

export function matches(item, filters) {
  return (!filters.asset || item.assets.includes(filters.asset)) &&
    (!filters.topic || item.topics.includes(filters.topic)) &&
    (!filters.publisher || item.publisher === filters.publisher) && item.importance >= Number(filters.importance);
}

export function mergeItems(old, incoming) {
  const items = new Map(old.map(item => [item.id, item]));
  incoming.forEach(item => { if (!items.has(item.id) || item.seq > items.get(item.id).seq) items.set(item.id, item); });
  return [...items.values()].sort((a, b) => b.seq - a.seq);
}

function savedFilters() {
  try {
    const value = JSON.parse(localStorage.getItem(KEY));
    return value && typeof value === "object" ? {
      asset: ["BTC", "ETH", "SOL", "XRP"].includes(value.asset) ? value.asset : "",
      topic: TOPICS.includes(value.topic) ? value.topic : "",
      publisher: ["CoinDesk", "Cointelegraph", "Federal Reserve"].includes(value.publisher) ? value.publisher : "",
      importance: [0, 50, 85, 95].includes(Number(value.importance)) ? Number(value.importance) : 0,
    } : EMPTY;
  } catch { return EMPTY; }
}

function time(value) {
  return value ? new Date(value).toLocaleString("en-US", {month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "UTC", timeZoneName: "short"}) : "Not yet available";
}

function FeedList({ title, items, loading, filtered }) {
  return <section className="rf-pane" aria-label={title}>
    <header><h2>{title}</h2><span>{items.length} {items.length === 1 ? "report" : "reports"}</span></header>
    {!items.length && <p className="rf-empty">{loading ? "Loading reports…" : filtered ? "No reports match these filters. Try a broader selection." : "No recent reports have arrived. Source status is shown above."}</p>}
    <ol>{items.map(item => <StoryRow key={item.id} item={item}>
      <p className="rf-observed">First seen {time(item.firstSeenAt)}{item.corrected && <> · Updated {time(item.updatedAt)}</>}</p>
    </StoryRow>)}</ol>
  </section>;
}

export default function ResearchFeed() {
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [sources, setSources] = useState([]);
  const [filters, setFilters] = useState(savedFilters);
  const [storageMessage, setStorageMessage] = useState("");
  const [tab, setTab] = useState("all");
  const [theme, changeTheme] = useResearchTheme();
  const [status, setStatus] = useState("Connecting");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [next, setNext] = useState(null);
  const [retry, setRetry] = useState(0);
  const [clock, setClock] = useState(Date.now());
  const cursor = useRef(0);
  const pageAbort = useRef(null);
  const restore = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(filters)); setStorageMessage("Filters saved in this browser."); }
    catch { setStorageMessage("Browser storage unavailable. Filters apply for this visit."); }
  }, [filters]);

  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    let reconnect;
    const delay = () => new Promise(resolve => { reconnect = setTimeout(resolve, 5000); });
    const read = async url => {
      const response = await fetch(url, {signal: controller.signal});
      if (!response.ok) throw new Error(response.status === 401 || response.status === 403 ? "Sign in again to read research." : "The feed is unavailable. Existing reports are retained.");
      return response;
    };
    async function start() {
      setError(""); setLoading(true); setLoadingMore(false);
      try {
        const data = await (await read(API)).json();
        if (disposed) return;
        setItems(data.items); setPending([]); setNext(data.nextCursor); setSources(data.sources); cursor.current = data.watermark;
        setLoading(false);
      } catch (failure) {
        if (!disposed) { setError(failure.message); setLoading(false); setStatus("Disconnected"); }
        return;
      }
      while (!disposed) {
        try {
          const response = await read(`${API}/events?after=${cursor.current}`);
          setStatus("Connected");
          if (!response.body) throw new Error("Streaming unavailable");
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (!disposed) {
            const {value, done} = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, {stream: true});
            let boundary;
            while ((boundary = buffer.indexOf("\n\n")) !== -1) {
              const block = buffer.slice(0, boundary); buffer = buffer.slice(boundary + 2);
              const lines = block.split("\n");
              const event = lines.find(line => line.startsWith("event: "))?.slice(7);
              const raw = lines.find(line => line.startsWith("data: "))?.slice(6);
              if (!raw) continue;
              const data = JSON.parse(raw);
              if (event === "item") { setPending(previous => mergeItems(previous, [data])); cursor.current = data.seq; }
              if (event === "health") setSources(data.sources);
            }
          }
        } catch { if (!disposed) setStatus("Reconnecting"); }
        if (!disposed) await delay();
      }
    }
    start();
    return () => { disposed = true; controller.abort(); pageAbort.current?.abort(); clearTimeout(reconnect); };
  }, [retry]);

  useEffect(() => {
    if (restore.current) {
      const {id, top} = restore.current;
      const anchor = [...document.querySelectorAll(`[data-feed-id="${id}"]`)].find(node => node.getBoundingClientRect().width > 0);
      if (anchor) window.scrollBy(0, anchor.getBoundingClientRect().top - top);
      restore.current = null;
    }
  }, [items, tab]);

  function rememberPosition() {
    const visible = [...document.querySelectorAll("[data-feed-id]")].find(node => node.getBoundingClientRect().bottom > 100 && node.getBoundingClientRect().width > 0);
    if (visible) restore.current = {id: visible.dataset.feedId, top: visible.getBoundingClientRect().top};
  }

  async function loadMore() {
    if (!next || loadingMore) return;
    const controller = new AbortController(); pageAbort.current = controller;
    setLoadingMore(true);
    try {
      const response = await fetch(`${API}?before=${next}`, {signal: controller.signal});
      if (!response.ok) throw new Error("Earlier reports could not be loaded. Please retry.");
      const data = await response.json(); setItems(previous => mergeItems(previous, data.items)); setNext(data.nextCursor); setError("");
    } catch (failure) { if (!controller.signal.aborted) setError(failure.message); }
    finally { if (!controller.signal.aborted) setLoadingMore(false); }
  }

  return <div className="research-desk research-feed" data-theme={theme}>
    <ResearchNav theme={theme} onThemeChange={changeTheme}/>
    <main className="rf-main"><SpringRise><header className="rf-intro"><div className="rd-kicker">AEON / Research wire</div><h1>Follow the story.</h1><p>Linked reports from our existing sources. New headlines arrive throughout the day; your reading position stays yours.</p><span className="rf-connection" role="status">{status} · Sources checked every five minutes</span></header></SpringRise>
      <section className="rf-health" aria-label="Source status">{sources.map(source => {
        const age = source.lastSuccessAt ? Math.max(0, Math.floor((clock - Date.parse(source.lastSuccessAt)) / 60000)) : null;
        const stale = source.status !== "ok" || age === null || age > 15;
        return <div key={source.publisher} className={stale ? "rf-stale" : ""}><strong>{source.publisher}</strong><span>{stale ? <StaleBadge>Delayed</StaleBadge> : "Available"} · Last successful check {age === null ? "not yet available" : `${age}m ago`}</span></div>;
      })}{!sources.length && <p>Waiting for the first source check.</p>}</section>
      <section className="rf-filters" aria-label="Saved feed filters"><div><h2>My Filters</h2><p>{storageMessage}</p></div>
        <label>Asset<select value={filters.asset} onChange={event => setFilters({...filters, asset: event.target.value})}><option value="">All assets</option>{["BTC", "ETH", "SOL", "XRP"].map(value => <option key={value}>{value}</option>)}</select></label>
        <label>Topic<select value={filters.topic} onChange={event => setFilters({...filters, topic: event.target.value})}><option value="">All topics</option>{TOPICS.map(value => <option key={value}>{value}</option>)}</select></label>
        <label>Source<select value={filters.publisher} onChange={event => setFilters({...filters, publisher: event.target.value})}><option value="">All sources</option>{["CoinDesk", "Cointelegraph", "Federal Reserve"].map(value => <option key={value}>{value}</option>)}</select></label>
        <label>Minimum importance<select value={filters.importance} onChange={event => setFilters({...filters, importance: Number(event.target.value)})}><option value={0}>All reports</option><option value={50}>50 / 100</option><option value={85}>85 / 100</option><option value={95}>95 / 100</option></select></label>
        <button className="rd-button" type="button" onClick={() => setFilters(EMPTY)}>Reset filters</button><small>Topics and importance are keyword-based sorting aids.</small>
      </section>
      {error && <p role="alert">{error} <button className="rd-button" type="button" onClick={() => setRetry(value => value + 1)}>Retry</button></p>}
      {!!pending.length && <button className="rf-updates" type="button" onClick={() => { rememberPosition(); setItems(previous => mergeItems(previous, pending)); setPending([]); }}>Show {pending.length} new or updated reports</button>}
      <div className="rf-tabs" role="group" aria-label="Feed view"><button type="button" aria-pressed={tab === "all"} onClick={() => { rememberPosition(); setTab("all"); }}>Everything</button><button type="button" aria-pressed={tab === "filtered"} onClick={() => { rememberPosition(); setTab("filtered"); }}>My Filters</button></div>
      <div className="rf-columns" data-tab={tab}><FeedList title="Everything" items={items} loading={loading} /><FeedList title="My Filters" items={items.filter(item => matches(item, filters))} loading={loading} filtered /></div>
      {next && <button className="rd-button rf-more" type="button" disabled={loadingMore} onClick={loadMore}>{loadingMore ? "Loading…" : "Load earlier reports"}</button>}
      <p className="rf-footnote">Headlines link to the publisher’s reporting. Publication time comes from the source; first seen is when AEON collected the link. Source updates are recorded as later revisions.</p>
    </main>
  </div>;
}
